import { useCallback, useEffect, useRef, useState } from 'react';
import { CssUnitSelect } from './CssUnitSelect';

/** @typedef {import('./CssUnitSelect').CssUnit} CssUnit */

const DEFAULT_UNITS = /** @type {CssUnit[]} */ (['px', 'em', '%', 'auto']);

/**
 * @typedef {object} DraggableNumberInputProps
 * @property {CssUnit}   [unit]           - Current unit
 * @property {CssUnit[]} [units]          - Which units to show
 * @property {number}    value            - Controlled value
 * @property {(value: number, unit: CssUnit|undefined) => void} onChange - Called on every value change
 * @property {number}   [min]            - Minimum allowed value
 * @property {number}   [max]            - Maximum allowed value
 * @property {number}   [step=1]         - How much to change per pixel dragged
 * @property {string}   [className]      - Extra class names for the wrapper
 */

/**
 * A number input with a drag handle on the right side.
 * Dragging the handle left/right decreases/increases the value.
 * Clicking the input directly allows manual keyboard entry.
 *
 * @param {DraggableNumberInputProps} props
 */
const DraggableNumberInput = ({
    value,
    unit,
    units,
    onChange,
    min,
    max,
    step = 1,
    className = '',
}) => {

    units = units ?? DEFAULT_UNITS;

    const [isDragging, setIsDragging] = useState(false);

    /* Local draft so the user can clear the field while typing.
       We only commit a number to onChange when the input parses cleanly. */
    const [draft, setDraft] = useState(String(value ?? ''));

    /* Sync the draft when `value` changes from outside (e.g. drag handle,
       another control, an undo). Skip if the draft already represents the
       same number — otherwise typing "12" would snap back to "12.0" etc. */
    useEffect(() => {
        if (Number(draft) !== value || draft === '') {
            setDraft(String(value ?? ''));
        }
        // Intentional: depending on `draft` here would clobber the user's
        // in-progress text on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    /** @type {React.MutableRefObject<number>} */
    const dragStartX = useRef(0);

    /** @type {React.MutableRefObject<number>} */
    const dragStartValue = useRef(0);

    /**
     * Clamps a value to [min, max] when those are defined.
     *
     * @param {number} v
     * @returns {number}
     */
    const clamp = useCallback((v) => {
        if (min !== undefined) v = Math.max(min, v);
        if (max !== undefined) v = Math.min(max, v);
        return v;
    }, [min, max]);

    /** @param {React.MouseEvent} e */
    const handleHandleMouseDown = (e) => {
        e.preventDefault();
        dragStartX.current = e.clientY;
        dragStartValue.current = value;
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return;

        /** @param {MouseEvent} e */
        const onMouseMove = (e) => {
            /* Invert so dragging up increases the value */
            const delta = dragStartX.current - e.clientY;
            const base = dragStartValue.current ?? 0;
            const next = clamp(
                /* round to avoid floating-point drift */
                Math.round((base + delta * step) / step) * step
            );
            onChange(next, unit);
        };

        const onMouseUp = () => setIsDragging(false);

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging, clamp, onChange, unit, step]);

    return (
        <div className={`draggable-number-input${isDragging ? ' dragging' : ''} ${unit === 'auto' ? 'auto' : ''} ${className}`.trim()}
            title={`${value}${unit ? unit : ''}`}
            >
            {unit !== 'auto' && <>
                <input
                    type="number"
                    className="draggable-number-input-field"
                    value={draft}
                    min={min}
                    max={max}
                    step={step}
                    onChange={(e) => {
                        const next = e.target.value;
                        setDraft(next);
                        /* Only commit a real number; allow the field to be empty
                           or mid-edit (e.g. "-", ".") without forcing it back to 0. */
                        if (next === '' || isNaN(Number(next))) return;
                        /* Commit the raw typed number — do NOT clamp here. Clamping
                           mid-keystroke fights the typist: with min=10, typing "3"
                           would jump to 10 and backspacing "10"→"1" would snap back
                           to "10". Clamp happens on blur instead. */
                        onChange(Number(next), unit);
                    }}
                    onBlur={() => {
                        if (draft === '' || isNaN(Number(draft))) {
                            /* Restore the last valid value when the user leaves
                               the field empty / invalid. */
                            setDraft(String(value ?? ''));
                            return;
                        }
                        /* Enforce min/max now that the user is done typing. */
                        const clamped = clamp(Number(draft));
                        setDraft(String(clamped));
                        onChange(clamped, unit);
                    }}
                />
                {/* Drag handle — mousedown starts scrubbing */}
                <span
                    className="draggable-number-input-handle"
                    onMouseDown={handleHandleMouseDown}
                    // style={{ cursor: 'ns-resize' }}
                    aria-hidden="true"
                    title="Sleep om te wijzigen"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="8"
                        height="12"
                        viewBox="0 0 8 12"
                        fill="currentColor"
                    >
                        {/* Up caret — click to increment */}
                        <path
                            d="M4 0 L8 4 L0 4 Z"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(clamp(value + step), unit);
                            }}
                            aria-label="Increment"
                            role="button"
                        />
                        {/* Down caret — click to decrement */}
                        <path
                            d="M4 12 L8 8 L0 8 Z"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange(clamp(value - step), unit);
                            }}
                            aria-label="Decrement"
                            role="button"
                        />
                    </svg>
                </span>
            </>}
            {unit && units && units.length > 0 && (
                <CssUnitSelect 
                    unit={unit ?? units[0]} 
                    onChange={(u) => onChange(value, u)}
                    units={units} 
                    className="draggable-number-input-unit" 
                />
            )}
        </div>
    );
};

export default DraggableNumberInput;
